<script lang="ts" setup>
import { projectService } from '@/services/project/project-service';
import { toProjectDisplayName } from '@/utils/projects/toProjectDisplayName';
import { computed, onMounted, ref } from 'vue';
import DropDown from '../DropDown.vue';

interface FetchedData {
  projects: Awaited<ReturnType<typeof projectService.getAll>>;
}

type Project = FetchedData['projects'][number];

export interface ProjectsDropDownSelectedProject {
  id: string;
  name: string;
  number: string;
  status?: Project['status'];
}

export interface ProjectsDropDownProps {
  data: {
    selectedProjectId?: string;
    excludeProjectIds?: string[];
    includeArchived?: boolean;
    placeholder?: string;
    up?: boolean;
    teleport?: boolean;
    filter?: (project: Project) => boolean;
  };
  mode?: 'disabled' | 'readonly' | 'select';
}

export type ProjectsDropDownAction = {
  type: 'select';
  project: ProjectsDropDownSelectedProject;
};

export type ProjectsDropDownEmits = {
  (event: 'action', payload: ProjectsDropDownAction): void;
  (event: 'update:modelValue', value: string): void;
};

const props = defineProps<ProjectsDropDownProps & { modelValue?: string }>();
const emit = defineEmits<ProjectsDropDownEmits>();
const fetchedData = ref<FetchedData>({
  projects: [],
});

const loadData = async () => {
  // TODO: Update this to fetch for specific user
  fetchedData.value.projects = await projectService.getAll();
};

const dropdownItems = computed(() => {
  return fetchedData.value.projects
    .filter((project) => !props.data?.excludeProjectIds?.includes(project.id))
    .filter(
      (project) => props.data.includeArchived || project.status !== 'Avslutat',
    )
    .filter((project) =>
      props.data.filter ? props.data.filter(project) : true,
    )
    .map((project) => ({
      value: project.id,
      text: toProjectDisplayName(project),
    }));
});

const mapProjectToSelectedProject = (
  project: Project,
): ProjectsDropDownSelectedProject => {
  return {
    id: project.id,
    name: project.name,
    number: project.fortnoxId || project.id,
    status: project.status,
  };
};

const onProjectSelect = (value: string | number | null) => {
  if (typeof value !== 'string' || value === '') {
    return;
  }

  const project = fetchedData.value.projects.find((item) => item.id === value);
  if (!project) {
    return;
  }

  const selectedProject = mapProjectToSelectedProject(project);
  emit('action', { type: 'select', project: selectedProject });
  emit('update:modelValue', selectedProject.id);
};

const selectedProjectDisplayName = computed(() => {
  const resolvedId = props.data.selectedProjectId ?? props.modelValue;
  const project = fetchedData.value.projects.find((p) => p.id === resolvedId);
  return project
    ? toProjectDisplayName(project)
    : (props.data.placeholder ?? '');
});

const resolvedMode = computed(() => props.mode ?? 'select');

onMounted(loadData);
</script>

<template>
  <span v-if="resolvedMode === 'readonly'" class="project-dropdown-readonly">
    {{ selectedProjectDisplayName || '-' }}
  </span>
  <div
    v-else
    class="project-dropdown-control"
    :class="{ 'is-disabled': resolvedMode === 'disabled' }"
  >
    <DropDown
      :items="dropdownItems"
      :label="props.data.placeholder"
      :model-value="props.data.selectedProjectId ?? props.modelValue ?? ''"
      :up="props.data.up"
      :teleport="props.data.teleport"
      :disabled="resolvedMode === 'disabled'"
      @update:model-value="onProjectSelect"
    />
  </div>
</template>

<style lang="scss" scoped>
.project-dropdown-readonly {
  display: block;
  min-height: var(--bulma-control-height);
  line-height: var(--bulma-control-height);
  color: #334155;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.project-dropdown-control {
  width: 100%;

  &.is-disabled {
    opacity: 0.85;
  }
}
</style>
